import { useEffect, useRef, useState } from 'react'
import {
  Box,
  Typography,
  IconButton,
  Stack,
  CircularProgress,
  Chip,
  Divider,
  Tooltip,
} from '@mui/material'
import LinkRoundedIcon from '@mui/icons-material/LinkRounded'
import KeyboardTabIcon from '@mui/icons-material/KeyboardTab'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { CARD_COLORS } from '@ds/theme/color-mode'
import type { TComment } from '@modules/types'
import { formatApiDateTime } from '@modules/api-core'
import { FilePreview } from '@modules/file-preview'
import { useList } from '@refinedev/core'
import {
  StatusPlaceholder,
  ChatInput,
  ChatSizeButton,
  MessageDocument,
  messageContentSx,
} from './components'
import { CHAT_STRINGS } from './strings'

const EXPANDED_SX = {
  position: 'fixed',
  top: 30,
  bottom: 30,
  left: 0,
  right: 0,
  mx: 'auto',
  width: 'calc(100% - 80px)',
  maxWidth: 1360,
  height: 'auto',
  zIndex: 'modal',
  boxShadow: 8,
} as const

export type TChatPanelProps = {
  commentsResource: string
  postUrl: string | null
  filesRequestId: number
  meta?: Record<string, unknown>
  onClose?: () => void
  title?: string
  threadId?: number | null
  height?: string
  findAuthor?: (comment: TComment) => string
  marks?: Record<string, string>
  linked?: { mark: string; tooltip: string }
  closed?: boolean
  closedText?: string
}

export const ChatPanel = ({
  commentsResource,
  postUrl,
  filesRequestId,
  meta,
  onClose,
  title = CHAT_STRINGS.title,
  threadId,
  height = '100%',
  findAuthor = comment => comment.commenter || (comment.isIncoming ? CHAT_STRINGS.client : CHAT_STRINGS.halLeonard),
  marks,
  linked,
  closed = false,
  closedText = CHAT_STRINGS.closed,
}: TChatPanelProps) => {
  const {
    data: commentsData,
    isLoading: isCommentsLoading,
    isError: isCommentsError,
    refetch: refetchComments,
  } = useList<TComment>({
    resource: commentsResource,
    meta,
    queryOptions: {
      enabled: Boolean(commentsResource),
    },
  })

  const comments = (commentsData?.data || []).filter(
    comment => threadId === undefined || comment.requestId === threadId,
  )
  const linkedIds: number[] = []
  let linking = false
  comments.forEach(comment => {
    if (comment.textDoc === null) linking = comment.textPlain === linked?.mark
    else if (linking && !comment.isIncoming) linkedIds.push(comment.id)
  })
  const messagesRef = useRef<HTMLDivElement>(null)
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    const messages = messagesRef.current
    if (messages) {
      messages.scrollTop = messages.scrollHeight
    }
  }, [comments.length])

  return (
    <Stack
      gap={2}
      bgcolor="background.paper"
      sx={{ height, px: 2.5, pt: 3.5, pb: 2.5, boxSizing: 'border-box', ...(expanded && EXPANDED_SX) }}
    >
      <Stack direction="row" alignItems="center">
        <Typography component="h2" sx={{ m: 0, flexGrow: 1, fontSize: 26, fontWeight: 500 }}>
          {title}
        </Typography>
        <ChatSizeButton expanded={expanded} onToggle={() => setExpanded(value => !value)} />
        {onClose && (
          <IconButton onClick={onClose} size="small" aria-label={CHAT_STRINGS.close}>
            <KeyboardTabIcon sx={{ transform: 'rotate(180deg)' }} />
          </IconButton>
        )}
      </Stack>

      <Stack
        ref={messagesRef}
        gap={2.25}
        sx={{ flex: 1, overflowY: 'auto', bgcolor: 'background.default', p: 2.25 }}
      >
        {isCommentsLoading && (
          <StatusPlaceholder
            type="loading"
            text="Loading chat…"
            icon={<CircularProgress color="primary" size={36} thickness={4} />}
          />
        )}
        {isCommentsError && (
          <StatusPlaceholder
            type="error"
            text="Failed to load chat"
            icon={<ErrorOutlineIcon color="error" sx={{ fontSize: 40 }} />}
          />
        )}
        {!isCommentsLoading &&
          !isCommentsError &&
          comments.map((comment: TComment) => {
            if (comment.textDoc === null && !marks?.[comment.textPlain]) return null
            return comment.textDoc === null ? (
              <Divider key={comment.id}>
                <Chip
                  size="small"
                  variant="outlined"
                  label={`${marks?.[comment.textPlain]} · ${formatApiDateTime(comment.createdAt)}`}
                />
              </Divider>
            ) : (
              <Box key={comment.id}>
                {findAuthor(comment) && (
                  <Typography sx={{ m: 0, mb: 0.75, fontSize: 13, color: CARD_COLORS.label }}>
                    {findAuthor(comment)}
                    {linked && linkedIds.includes(comment.id) && (
                      <Tooltip title={linked.tooltip}>
                        <LinkRoundedIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle' }} />
                      </Tooltip>
                    )}
                  </Typography>
                )}
                <Stack
                  gap={1}
                  sx={{
                    minWidth: 0,
                    bgcolor: 'background.paper',
                    borderRadius: 1.5,
                    boxShadow: '0 1px 3px rgba(0,0,0,.12)',
                    px: 2,
                    py: 1.75,
                  }}
                >
                  <Box
                    sx={{
                      ...messageContentSx,
                      fontSize: 15,
                      lineHeight: 1.21,
                      '& table': {
                        display: 'block',
                        overflowX: 'auto',
                        maxWidth: '100%',
                        borderCollapse: 'collapse',
                        my: 1,
                      },
                    }}
                  >
                    <MessageDocument doc={comment.textDoc} />
                  </Box>
                  {comment.files?.map(file => (
                    <FilePreview key={file.id} file={file} requestId={filesRequestId} variant="chat" />
                  ))}
                  <Typography
                    sx={{ m: 0, alignSelf: 'flex-end', fontSize: 12, color: CARD_COLORS.muted }}
                  >
                    {formatApiDateTime(comment.createdAt)}
                  </Typography>
                </Stack>
              </Box>
            )
          })}
      </Stack>

      {closed && (
        <Stack
          direction="row"
          alignItems="center"
          gap={1.25}
          sx={{
            border: 1,
            borderColor: CARD_COLORS.closedBorder,
            borderRadius: 1,
            bgcolor: CARD_COLORS.closedBg,
            px: 2,
            py: 2.25,
            fontSize: 15,
            color: CARD_COLORS.withdrawnValue,
          }}
        >
          <LockOutlinedIcon sx={{ fontSize: 18 }} />
          {closedText}
        </Stack>
      )}
      {postUrl && !closed && (
        <ChatInput
          url={postUrl}
          framed={false}
          expanded={expanded}
          onSent={async () => {
            await refetchComments()
          }}
        />
      )}
    </Stack>
  )
}
